import { Color } from '@components/Color';
import { Currency } from '@components/Currency';
import { InlineBlock } from '@components/Layout/Blocks';
import { Col } from '@components/Layout/Col';
import { Row } from '@components/Layout/Row';
import { ItemType } from '@components/SummarySelection/data/selectors';
import { useMediaQuery } from '@hooks/useMediaQuery';
import CircularProgress from '@material-ui/core/CircularProgress';
import Icon from '@material-ui/core/Icon/Icon';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField/TextField';
import Typography from '@material-ui/core/Typography/Typography';
import { call } from '@packages/functions';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { InstantQuoteContext } from '../../InstantQuote/Stepper/InstantQuoteStepper';

const SItem = styled.div`
    && {
        width: 100%;
        position: relative;
        margin: 15px 0;
        padding-bottom: 15px;
        padding-left: 8px;
        padding-right: 8px;
        &:after {
            content: ' ';
            position: absolute;
            bottom: 0;
            left: 14px;
            right: 14px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.2);
        }

        &:last-child {
            margin-bottom: 0;
            &:after {
                border-bottom: 0;
            }
        }

        .item-headline {
            margin: 10px 0;
        }

        .item--sku-name {
            font-size: 16px;
            font-weight: 500;
            margin-top: 10px;
            padding: 0;
        }

        .item--sku-id {
            color: #555;
            font-size: 14px;
        }
    }
`;

const ItemImage = styled.div`
    && {
        &,
        img {
            width: 120px;
            height: 120px;
            border-radius: 3px;
        }
        img {
            border-right: 1px solid #fafafa;
            object-fit: contain;
        }
    }
`;

const SItemEditOneLineQuantityHeadline = styled(Row)`
    .ui--col {
        font-size: 16px;
        font-weight: 500;
        margin-top: 20px;
        color: #555;
    }
`;

const Headline = styled(Typography)`
    && {
        margin: 0 0 8px;
    }
`;

interface IItemEditOneLineQuantityProps {
    item: ItemType;
    onUpdate: (...args: any) => any;
    onDelete?: (...args: any) => any;
    index: number;
    showHeadline?: boolean;
    disableInputs?: boolean;
    showTierPricing?: boolean;
}

/**
 * @author: Stefan Binder <stefan.binder89@gmail.com>
 * @class: Home
 * @date:  29.08.2018
 * @time:  08:43
 * @scope:
 */
export default function ItemEditOneLineQuantity(props: IItemEditOneLineQuantityProps) {
    const { item } = props;

    const isMobile = useMediaQuery('mobile');

    /**
     * Take the first itemSpec in item_specifications, if doesn't exist we create it.
     * In that component, there is only ONE item-specification (no names, numbers, etc.)
     */
    const defaultItemSpecification =
        item.item_specifications && item.item_specifications.length > 0 ? item.item_specifications[0] : { quantity: 1 };

    const [itemSpecification, setItemSpecification] = useState(defaultItemSpecification);
    const [isChanged, setIsChanged] = useState(false);
    const { instantQuote, dispatch } = useContext(InstantQuoteContext);

    useEffect(() => {
        dispatch({ type: 'recalculate', payload: { id: item.id, quantity: itemSpecification.quantity } });
    }, []);

    const itemPrices = instantQuote.prices.find((price) => {
        if (item.package) {
            return price.type === 'package' && price.type_id === item.package.id;
        } else if (item.selectable_sku) {
            return price.type === 'selectable_sku' && price.type_id === item.selectable_sku.id;
        } else if (item.sku) {
            return price.type === 'sku' && price.type_id === item.sku.id;
        }
        return false;
    });

    const handleChange = (event) => {
        if (event.target.name === 'quantity') {
            event.target.value = parseInt(`${event.target.value}`.replace(/\D/g, ''));
            if (event.target.value > 10000) {
                event.target.value = 10000;
            }
        }

        if (itemSpecification[event.target.name] !== event.target.value) {
            setIsChanged(true);
            setItemSpecification({
                ...itemSpecification,
                [event.target.name]: event.target.value,
            });
        }
    };

    const handleUpdate = (e, forced = false) => {
        if ((forced || isChanged) && itemSpecification.quantity > 0) {
            props.onUpdate({
                ...item,
                item_specifications: [itemSpecification],
            });

            dispatch({ type: 'recalculate', payload: { id: item.id, quantity: itemSpecification.quantity } });

            setIsChanged(false);
        }
    };

    const handleDelete = () => {
        call(props.onDelete, item);
    };

    const [details$, image$] = renderItemDetailsAndImage(item, { isMobile });
    let title$: any = null;

    if (props.showHeadline) {
        const title = item && !item.package ? 'Extra Option #' + props.index : 'Base Apparel';

        title$ = (
            <Row>
                <Col xs={12}>
                    <Headline variant={'subtitle1'} color={'textSecondary'}>
                        {title}
                    </Headline>
                </Col>
            </Row>
        );
    }

    let priceEach$: any = null;
    if (itemPrices) {
        itemPrices['price_single'] = parseFloat(itemPrices['price_single']);
        itemPrices['price_single_high'] = parseFloat(itemPrices['price_single_high']);

        if (itemPrices['price_single'] !== itemPrices['price_single_high'] && itemPrices['price_single_high'] > 0) {
            priceEach$ = (
                <>
                    <Currency suffix="/Each" style={{ textDecoration: 'line-through', marginRight: 8 }}>
                        {itemPrices['price_single_high']}
                    </Currency>
                    <Currency suffix="/Each" color={'success'}>
                        {itemPrices['price_single']}
                    </Currency>
                </>
            );
        } else {
            priceEach$ = <Currency suffix="/Each">{itemPrices['price_single']}</Currency>;
        }
    }

    const isCalculating =
        instantQuote.recalculate && instantQuote.recalculate_queue.find((queueItem) => queueItem.id === item.id);

    const handleNextTier = (qt) => () => {
        const newValue = parseInt(itemSpecification.quantity) + qt;

        handleChange({
            target: {
                value: newValue,
                name: 'quantity',
            },
        });

        itemSpecification.quantity = newValue;

        setTimeout(handleUpdate, null, true);
    };

    return (
        <SItem>
            {title$}
            <Row>
                <Col
                    xs={12}
                    sm={12}
                    md={6}
                    layout={isMobile ? 'column' : 'row'}
                    align={isMobile ? 'center' : 'start'}
                    justify={isMobile ? 'center' : 'start'}
                >
                    <InlineBlock w={120}>
                        <ItemImage>{image$}</ItemImage>
                    </InlineBlock>
                    <InlineBlock maxWidth={'calc(100% - 120px)'}>
                        {details$}
                        <Row>
                            <Col xs={12} padding={'8px 14px'} justify={isMobile ? 'center' : 'start'}>
                                {priceEach$}
                            </Col>
                        </Row>
                    </InlineBlock>
                </Col>
                <Col xs={4} sm={4} md={2} align={'start'} justify={isMobile ? 'start' : 'center'} padding={'20px 14px'}>
                    {isCalculating ? (
                        <CircularProgress color={'primary'} size={20} />
                    ) : (
                        <Currency color={'success'}>{itemPrices ? itemPrices['price'] : 0}</Currency>
                    )}
                </Col>
                <Col xs={8} sm={8} md={4} align={'start'} justify={'end'}>
                    <InlineBlock maxWidth={'calc(100% - 64px)'} w={'100%'}>
                        <TextField
                            value={itemSpecification.quantity}
                            disabled={props.disableInputs}
                            onChange={handleChange}
                            onBlur={handleUpdate}
                            fullWidth={true}
                            id={'quantity'}
                            label={'QTY.'}
                            name={'quantity'}
                            type={'number'}
                            margin={'dense'}
                            variant={'outlined'}
                        />

                        {props.showTierPricing && itemPrices && itemPrices['next_tier_quantity'] !== 0 ? (
                            <span style={{ fontSize: '12px' }}>
                                Add
                                <Color
                                    component={'a'}
                                    color={'success'}
                                    style={{ fontSize: 12, cursor: 'pointer', margin: '0 4px' }}
                                    onClick={handleNextTier(itemPrices['next_tier_quantity'])}
                                >
                                    {itemPrices['next_tier_quantity']}
                                </Color>
                                more to un-lock next tier pricing and save!
                            </span>
                        ) : null}
                    </InlineBlock>

                    <InlineBlock w={64} p={'8px 0 0 16px'}>
                        {item &&
                        (item.sku || item.selectable_sku) &&
                        !props.disableInputs &&
                        typeof props.onDelete === 'function' ? (
                            <IconButton onClick={handleDelete} size={'small'}>
                                <Icon>delete_outline</Icon>
                            </IconButton>
                        ) : null}
                    </InlineBlock>
                </Col>
            </Row>
        </SItem>
    );
}

export const ItemEditOneLineQuantityHeadline = (props: any) => (
    <SItemEditOneLineQuantityHeadline>
        <Col xs={6}>Items {props.count ? `(${props.count})` : ''}</Col>
        <Col xs={2}>Price / Piece</Col>
        <Col xs={2}>Total Price</Col>
        <Col xs={2}>QTY.</Col>
    </SItemEditOneLineQuantityHeadline>
);

function renderItemDetailsAndImage(item, { isMobile }: any = {}) {
    let details$: any = null;
    let image$: any = null;

    // $FlowFixMe
    const sku = item ? item.sku || item.selectable_sku : null;

    if (item && sku && !item.package) {
        /**
         * The item has a # SKU #
         */
        details$ = (
            <Row>
                <Col xs={12} justify={isMobile ? 'center' : 'start'}>
                    <Typography variant={'h2'} display={'inline'} className={'item--sku-name'}>
                        {sku.name}
                    </Typography>
                </Col>
                <Col xs={12} justify={isMobile ? 'center' : 'start'}>
                    <Typography variant={'body1'} display={'inline'} className={'item--sku-id'}>
                        {sku.internal_id}
                    </Typography>
                </Col>
            </Row>
        );

        if (sku.preview_photo) {
            image$ = <img src={sku.preview_photo.thumbnail_path} alt={sku.name} />;
        }
    } else if (item && item.package) {
        /**
         * The item has a # PACKAGE #
         */
        details$ = (
            <Row>
                <Col xs={12} justify={isMobile ? 'center' : 'start'}>
                    <Typography variant={'h2'} display={'inline'} className={'item--sku-name'}>
                        {item.package.name}
                    </Typography>
                </Col>
                <Col xs={12} justify={isMobile ? 'center' : 'start'}>
                    <Typography variant={'body1'} display={'inline'} className={'item--sku-id'}>
                        {(item.package.subcategories || []).map(({ default_sku }) => default_sku.name).join(', ')}
                    </Typography>
                </Col>
            </Row>
        );

        if (item.package.image) {
            image$ = <img src={item.package.image.thumbnail_path} alt={item.package.name} />;
        }
    }

    if (!image$) {
        image$ = <img src={'/img/landings/logo-v2.png'} alt={''} />;
    }

    return [details$, image$];
}
